import { BillingAddressType } from 'features/Billing'
import { omit } from 'lodash-es'
import { getSnapshot, types } from 'mobx-state-tree'
import { isEUCountryCode } from 'service/ISO'
import { isAlphanumeric, isLength } from 'validator'

import {
  FormBooleanFieldModel,
  FormStringFieldModel,
  FormStringFieldWithDisabledModel,
} from '../FormField/FormFieldModel'
import { IFormValidation } from '../FormField/FormValidation'
import { Address } from '../Locations/LocationAddress'
import { BillingAddressModel } from './BillingAddressModel'
import { BillingContact, IBillingContact } from './BillingContactModel'

export enum LegalEntityRegion {
  EU = 'EU',
  NON_EU = 'NON_EU',
}

export interface ILegalEntityResJSON {
  data: ILegalEntityJSON[]
  total: number
}

type LegalEntityV3BillingAddress = {
  street: string
  city: string
  countryCode: string
  postalCode: string
  state: string
  country: string
  street2: string
}

export type LegalEntityV3DTO = {
  code: string
  name: string
  accountID: string
  billing: {
    address?: LegalEntityV3BillingAddress
  }
}

export const getFullLegalEntityV3Address = (address?: LegalEntityV3BillingAddress) => {
  if (!address) {
    return ''
  }

  const { city, country, postalCode, state, street, street2 } = address

  return [street, street2, city, state, postalCode, country].filter(Boolean).join(', ')
}

export interface ILegalEntityJSON {
  _id: string
  customerId: string
  organizationId: string
  billingEntityId?: string
  name: string
  billingContact: IBillingContact
  hasVat: boolean
  region: LegalEntityRegion
  vatId?: string
  eoriNumber: string
  billingAddress: Address
  notes?: string
  defaultForIntegrations?: boolean
  createdAt: string
  updatedAt: string
  deletedAt?: string
  taxId?: string
  paCode?: string
  pecEmailAddress?: string
}

// NOTE: Below error is thrown when Legal Entity has tried to be deleted, but is selected in some of the active shipments
export const LegalEntityActiveErrorCode = 'legal-entity-is-active-error'
export interface LegalEntityActiveError {
  code: typeof LegalEntityActiveErrorCode
  legalEntityId: string
  shipmentIds: string[]
}

const dirtyEnabledFields = ['organizationId', 'billingEntityId', 'name', 'eoriNumber', 'vatId', 'notes', 'taxId']

export const LegalEntity = types
  .model('LegalEntity', {
    _id: types.maybe(types.identifier(types.string)),
    organizationId: types.optional(FormStringFieldModel, undefined),
    billingEntityId: FormStringFieldModel,
    name: FormStringFieldWithDisabledModel,
    billingContact: types.optional(BillingContact, {}),
    eoriNumber: FormStringFieldModel,
    vatId: FormStringFieldModel,
    billingAddress: types.optional(BillingAddressModel, {}),
    notes: FormStringFieldModel,
    defaultForIntegrations: types.optional(FormBooleanFieldModel, undefined),
    createdAt: types.maybe(types.string),
    updatedAt: types.maybe(types.string),
    deletedAt: types.maybe(types.string),
    region: types.optional(
      types.union(types.literal(LegalEntityRegion.EU), types.literal(LegalEntityRegion.NON_EU)),
      LegalEntityRegion.EU
    ),
    hasVat: types.optional(types.boolean, true),
    taxId: types.optional(types.union(FormStringFieldModel, types.null), undefined),
    paCode: types.optional(types.union(types.string, types.undefined, types.null), undefined),
    pecEmailAddress: types.optional(types.union(types.string, types.undefined, types.null), undefined),
  })
  .volatile(() => ({
    hasValidVat: false,
    hasDuplicateVatIdAndName: false,
    hasDuplicateTaxIdAndName: false,
    triedSubmit: false,
  }))
  .views((self) => ({
    validateOrganizationId(): IFormValidation {
      return {
        isInvalid: self.organizationId.isDirty && !self.organizationId.value,
        message: 'directory-validation_organization_select',
      }
    },
    validateBillingEntityId(): IFormValidation {
      return {
        isInvalid: self.billingEntityId.isDirty && !self.billingEntityId.value,
        message: 'directory-validation_billing_entity_select',
      }
    },
    validateName(): IFormValidation {
      return {
        isInvalid: self.name.isDirty && !isLength(self.name.value, { min: 1, max: 100 }),
        message: 'legal-entity-validation_name',
      }
    },
    validateEori(): IFormValidation {
      return {
        isInvalid:
          self.eoriNumber.isDirty && self.region === LegalEntityRegion.EU && !isAlphanumeric(self.eoriNumber.value),
        message: 'directory-validation_eori',
      }
    },
    validateVatId(): IFormValidation {
      return {
        isInvalid: self.vatId.isDirty && self.hasVat && (!self.hasValidVat || self.hasDuplicateVatIdAndName),
        message: self.hasDuplicateVatIdAndName ? 'legal-entity-vatid-and-name-taken' : 'directory-validation_vat',
      }
    },
    validateNotes(): IFormValidation {
      return {
        isInvalid: !isLength(self.notes.value, { min: 0, max: 500 }),
        message: 'directory-validation_notes',
      }
    },
    validateTaxId(): IFormValidation {
      return {
        isInvalid:
          self.taxId!.isDirty &&
          !self.hasVat &&
          (!isLength(self.taxId!.value, { min: 1 }) || self.hasDuplicateTaxIdAndName),
        message: self.hasDuplicateTaxIdAndName ? 'legal-entity-taxid-and-name-taken' : 'directory-validation_taxid',
      }
    },
  }))
  .views((self) => ({
    get isValid(): boolean {
      const validationField = [
        'validateName',
        'validateOrganizationId',
        'validateBillingEntityId',
        'validateEori',
        'validateVatId',
        'validateNotes',
        'validateTaxId',
      ]

      return validationField.every((field) => !self[field]().isInvalid)
    },
  }))
  .views((self) => ({
    get isChangedAndValid(): boolean {
      return (
        self.isValid &&
        self.organizationId.isDirty &&
        self.name.isDirty &&
        self.eoriNumber.isDirty &&
        self.vatId.isDirty &&
        self.billingContact.isValid &&
        self.billingAddress.isValid
      )
    },
    get legalEntityJSON(): Partial<ILegalEntityJSON> {
      const pristineSnapshot = getSnapshot(self)
      const snapshot = self.hasVat ? omit(pristineSnapshot, ['taxId']) : omit(pristineSnapshot, ['vatId'])
      const fieldsToRemove = ['createdAt', 'updatedAt', 'deletedAt']

      if (snapshot._id) {
        return omit(snapshot, fieldsToRemove)
      }

      return omit(snapshot, [...fieldsToRemove, '_id'])
    },
    get billingInformation(): {
      billingAddress: BillingAddressType
      eoriNumber: string
    } {
      const pristineSnapshot = getSnapshot(self)
      // for now taxId is also represented as vatId, will be changed in the future when BE supports taxId for booking
      const vatId = pristineSnapshot.vatId ? pristineSnapshot.vatId : pristineSnapshot.taxId
      const snapshot = omit(pristineSnapshot, ['taxId', 'vatId'])
      return {
        eoriNumber: snapshot.eoriNumber,
        billingAddress: {
          ...snapshot.billingAddress,
          ...snapshot.billingContact,
          companyName: snapshot.name,
          vatId,
        },
      }
    },
    get isEU(): boolean {
      return self.region === LegalEntityRegion.EU
    },
    get hasPaCode(): boolean {
      return !!self.paCode
    },
    get hasPecEmailAddress(): boolean {
      return !!self.pecEmailAddress
    },
  }))
  .actions((self) => ({
    postProcessSnapshot(pristineSnapshot) {
      const snapshot = self.hasVat ? omit(pristineSnapshot, ['taxId']) : omit(pristineSnapshot, ['vatId'])
      if (snapshot._id) {
        return snapshot
      }
      return omit(snapshot, ['_id'])
    },
    enableFields(): void {
      self.name.setDisabled(false)
    },
  }))
  .actions((self) => ({
    reset(): void {
      self.enableFields()
      self.billingContact.reset()
      self.billingAddress.reset()
      dirtyEnabledFields.forEach((dirtyEnabledField) => {
        self[dirtyEnabledField].isDirty = false
      })
      self.triedSubmit = false
      self.hasDuplicateVatIdAndName = false
      self.hasDuplicateTaxIdAndName = false
    },
  }))
  .actions((self) => ({
    afterCreate(): void {
      self.hasValidVat = !!self.vatId.value
    },
    setOrganizationId(id: string): void {
      self.organizationId.setValue(id)
    },
    setBillingEntityId(id: string): void {
      self.billingEntityId.setValue(id)
    },
    setName(name: string): void {
      if (self.hasDuplicateVatIdAndName && name !== self.name.value) {
        self.hasDuplicateVatIdAndName = false
      }
      if (self.hasDuplicateTaxIdAndName && name !== self.name.value) {
        self.hasDuplicateTaxIdAndName = false
      }
      self.name.setValue(name)
    },
    setEori(eori: string): void {
      self.eoriNumber.setValue(eori)
    },
    setDefaultForIntegrations(defaultForIntegrations: boolean): void {
      self.defaultForIntegrations.setValue(defaultForIntegrations)
    },
    setVatId(vatId: string): void {
      if (self.hasDuplicateVatIdAndName && vatId !== self.vatId.value) {
        self.hasDuplicateVatIdAndName = false
        self.triedSubmit = false
      }
      self.vatId.setValue(vatId)
    },
    setNotes(notes: string): void {
      self.notes.setValue(notes)
    },
    setRegion(region: LegalEntityRegion) {
      self.region = region
      if (self.region === LegalEntityRegion.NON_EU) {
        self.hasVat = false
      } else {
        self.hasVat = true
        self.hasValidVat = false
      }
      // If country selected was EU, but new region is NON_EU
      if (
        self.billingAddress.countryCode.value?.length &&
        self.region === LegalEntityRegion.NON_EU &&
        isEUCountryCode(self.billingAddress.countryCode.value)
      ) {
        self.billingAddress.countryCode.setValue('')
        self.billingAddress.country.setValue('')
      }
      // If country selected was NON_EU, but new region is EU
      if (
        self.billingAddress.countryCode.value?.length &&
        self.region === LegalEntityRegion.EU &&
        !isEUCountryCode(self.billingAddress.countryCode.value)
      ) {
        self.billingAddress.countryCode.setValue('')
        self.billingAddress.country.setValue('')
      }
    },
    validate(): void {
      self.billingContact.validate()
      self.billingAddress.validate()
      dirtyEnabledFields.forEach((dirtyEnabledField) => {
        self[dirtyEnabledField].isDirty = true
      })
    },
    setHasVat(hasVat: boolean): void {
      self.hasVat = hasVat
    },
    setHasValidVat(hasValidVat: boolean): void {
      self.hasValidVat = hasValidVat
    },
    setHasDuplicateVatIdAndName(hasDuplicate: boolean): void {
      self.hasDuplicateVatIdAndName = hasDuplicate
    },
    setHasDuplicateTaxIdAndName(hasDuplicate: boolean): void {
      self.hasDuplicateTaxIdAndName = hasDuplicate
    },
    setTaxId(taxId: string): void {
      if (self.hasDuplicateTaxIdAndName && taxId !== self.taxId?.value) {
        self.hasDuplicateTaxIdAndName = false
      }
      self.taxId!.setValue(taxId)
    },
    setTriedSubmit(triedSubmit: boolean): void {
      self.triedSubmit = triedSubmit
    },
  }))
export type TLegalEntity = typeof LegalEntity.Type

export const LegalEntitiesModel = types.model('LegalEntitiesModel', {
  legalEntities: types.array(LegalEntity),
})
export type TLegalEntitiesModel = typeof LegalEntitiesModel.Type
